import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch, buildParams } from "@/lib/api";
import { homeQueryKeys } from "@/lib/homeQueryCache";
import type { AviFavoriteToggleOutput, AviListParams, PaginatedAviListOutput } from "@/types";

export function useFavoriteAvi(
  accountId: string,
  listParams: AviListParams,
  paaUuid: string,
  role: string
) {
  const queryClient = useQueryClient();
  const listKey = [...homeQueryKeys.aviList, listParams];
  const favoritesKey = [...homeQueryKeys.aviFavorites, listParams];

  return useMutation({
    mutationFn: () =>
      apiFetch<AviFavoriteToggleOutput>(
        `/v1/home/avi-favorite/${paaUuid}${buildParams({ account_id: accountId, role: role })}`,
        { method: "PUT" }
      ),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: listKey });
      await queryClient.cancelQueries({ queryKey: favoritesKey });

      const previous = queryClient.getQueryData<PaginatedAviListOutput>(listKey);
      const previousFavorites = queryClient.getQueryData<PaginatedAviListOutput>(favoritesKey);

      queryClient.setQueryData<PaginatedAviListOutput>(listKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item) =>
            item.paa_uuid === paaUuid ? { ...item, is_favorite: !item.is_favorite } : item
          ),
        };
      });

      queryClient.setQueryData<PaginatedAviListOutput>(favoritesKey, (old) => {
        if (!old) return old;
        const target = old.items.find((item) => item.paa_uuid === paaUuid);
        if (!target) {
          // Item not in the favorites page yet (e.g. toggled from the All tab).
          // Find it in the pre-update avi-list snapshot and inject it as a favorite.
          const fromList = previous?.items.find((item) => item.paa_uuid === paaUuid);
          if (!fromList || fromList.is_favorite) return old;
          return {
            ...old,
            items: [...old.items, { ...fromList, is_favorite: true }],
            total: old.total + 1,
          };
        }
        // Items in the favorites cache always have is_favorite === true; reaching here means un-favoriting.
        return {
          ...old,
          items: old.items.filter((item) => item.paa_uuid !== paaUuid),
          total: old.total - 1,
        };
      });

      return { previous, previousFavorites };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(listKey, context?.previous);
      queryClient.setQueryData(favoritesKey, context?.previousFavorites);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listKey });
      // Intentionally broad: invalidates all favorites queries regardless of param set,
      // so the favorites tab always refetches after a toggle.
      queryClient.invalidateQueries({ queryKey: homeQueryKeys.aviFavorites });
      queryClient.invalidateQueries({ queryKey: homeQueryKeys.aviDeadline });
      queryClient.invalidateQueries({ queryKey: homeQueryKeys.workflow });
    },
  });
}
